import { makeObservable, observable, runInAction } from 'mobx'

import {
  DeleteDraftPermitsDocument,
  DeleteFormsDocument,
  DeleteThisAndFutureFormsDocument,
} from '~/client/graph/operations/generated/SitePermits.generated'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Workflow from '~/client/src/shared/models/Workflow'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import { toast } from '~/client/src/shared/utils/toast'

import { IMutationResult } from '../../utils/GraphExecutor'
import InitialState from '../InitialState'
import GraphExecutorStore from './GraphExecutor.store'
import MarkupsStore from './Markups.store'

export default class WorkflowsStore {
  @observable public isDataReceived = true

  public constructor(
    private eventsStore: EventsStore,
    private graphExecutorStore: GraphExecutorStore,
    private markupsStore: MarkupsStore,
  ) {
    makeObservable(this)
  }

  /** @deprecated Permits are now loaded on-demand via useWorkflows hook */
  public getFormById = (id: string): Workflow | undefined => {
    void id
    return undefined
  }

  public deleteWorkflows = async (
    permits: Workflow[],
    thisAndFutureRecurring: boolean = false,
  ) => {
    runInAction(() => {
      this.state.loading.set(e.DELETE_WORKFLOWS, true)
    })

    let res: IMutationResult
    let draftRes: IMutationResult
    const deletedFormIds: string[] = []

    const draftPermits = permits.filter(p => p.isDraft)
    const nonDraftPermits = permits.filter(p => !p.isDraft)

    if (draftPermits.length) {
      draftRes = await this.deleteDraftPermits(draftPermits)
      const deletedIds = draftRes?.data?.deleteManyDraftPermits || []
      deletedFormIds.push(...deletedIds)
    }

    if (nonDraftPermits.length) {
      if (!thisAndFutureRecurring) {
        res = await this.deletePermits(nonDraftPermits)
        deletedFormIds.push(...(res?.data?.softDeleteManyForms || []))
      } else {
        res = await this.deleteRecurringPermits(nonDraftPermits)
        deletedFormIds.push(...(res?.data?.deleteThisAndFutureForms || []))
      }
    }

    runInAction(() => {
      this.state.loading.set(e.DELETE_WORKFLOWS, false)
    })

    if (draftRes?.error || res?.error) {
      toast.error(Localization.translator.failedToDelete)
      return false
    }

    this.removeMarkupsForDeletedForms(deletedFormIds)

    this.showSuccessToast(deletedFormIds.length)

    // An empty list is a no-op delete (already gone, or refused server-side): no
    // toast fires, so the caller must not treat it as done either.
    return !!deletedFormIds.length
  }

  public deleteForms = this.deleteWorkflows

  // The backend soft-deletes a workflow's markups in the same delete transaction;
  // drop them from the shared in-memory map so they vanish from the map immediately
  // (otherwise they linger until the next full reload re-queries without them).
  private removeMarkupsForDeletedForms(deletedFormIds: string[]) {
    if (!deletedFormIds.length) {
      return
    }

    deletedFormIds.forEach(id =>
      this.markupsStore.removeLocal(
        this.markupsStore.getBySitePermitId(id).map(markup => markup.id),
      ),
    )
  }

  private deleteRecurringPermits = async (
    permits: Workflow[],
  ): Promise<IMutationResult> => {
    return await this.graphExecutorStore.mutate(
      DeleteThisAndFutureFormsDocument,
      {
        id: permits[0].id,
      },
    )
  }

  private deletePermits = async (
    permits: Workflow[],
  ): Promise<IMutationResult> => {
    return await this.graphExecutorStore.mutate(DeleteFormsDocument, {
      ids: permits.map(p => p.id),
    })
  }

  private deleteDraftPermits = async (
    permits: Workflow[],
  ): Promise<IMutationResult> => {
    return await this.graphExecutorStore.mutate(DeleteDraftPermitsDocument, {
      ids: permits.map(p => p.id),
    })
  }

  private showSuccessToast(deletedFormsCount: number) {
    if (deletedFormsCount) {
      toast.success(
        Localization.translator.successfullyDeletedForms(deletedFormsCount),
      )
    }
  }

  public get isDeleting(): boolean {
    return this.state.loading.get(e.DELETE_WORKFLOWS)
  }

  private get state(): InitialState {
    return this.eventsStore.appState
  }
}
